import { useRef, useState } from "react";
import type { Tenant } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// El logo se sirve siempre desde el endpoint público por slug
// (/api/publico/estudio/:slug/logo) — es una imagen, no información
// sensible, y reutilizar la misma ruta evita duplicar el streaming desde
// R2 en una ruta autenticada aparte. Acá sólo se sube/reemplaza/elimina.
export function PanelIdentidadEstudio({
  tenant,
  puedeEditar,
  onCambio,
}: {
  tenant: Tenant;
  puedeEditar: boolean;
  onCambio: (tenant: Tenant) => void;
}) {
  const { mostrar } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [cacheBuster, setCacheBuster] = useState(0);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);

  async function subirArchivo(archivo: File) {
    setSubiendo(true);
    try {
      const formData = new FormData();
      formData.append("archivo", archivo);
      const datos = await api.subirArchivo<{ tenant: Tenant }>("/configuracion/logo", formData);
      onCambio(datos.tenant);
      setCacheBuster((c) => c + 1);
      mostrar("Logo actualizado.", "exito");
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo subir el logo.", "error");
    } finally {
      setSubiendo(false);
    }
  }

  async function eliminarLogo() {
    setSubiendo(true);
    try {
      const datos = await api.delete<{ tenant: Tenant }>("/configuracion/logo");
      onCambio(datos.tenant);
      mostrar("Logo eliminado.", "exito");
      setConfirmandoEliminar(false);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo eliminar el logo.", "error");
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-texto">Identidad del Estudio</h3>
      <p className="mb-3 text-xs text-texto-secundario">
        El logo se muestra en el menú lateral y en la pantalla de acceso. Si no hay logo cargado, se
        usa el ícono por defecto.
      </p>
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-borde bg-fondo">
          {tenant.logo_r2_key ? (
            <img
              key={cacheBuster}
              src={`/api/publico/estudio/${tenant.slug}/logo?v=${cacheBuster}`}
              alt="Logo del estudio"
              className="h-full w-full rounded-lg object-contain p-1"
            />
          ) : (
            <span className="text-lg font-semibold text-texto-secundario">J</span>
          )}
        </div>
        {puedeEditar && (
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={(e) => {
                const archivo = e.target.files?.[0];
                if (archivo) subirArchivo(archivo);
                e.target.value = "";
              }}
            />
            <Boton
              type="button"
              variante="secundario"
              tamano="sm"
              cargando={subiendo}
              onClick={() => inputRef.current?.click()}
            >
              {tenant.logo_r2_key ? "Reemplazar logo" : "Subir logo"}
            </Boton>
            {tenant.logo_r2_key && (
              <Boton
                type="button"
                variante="peligro"
                tamano="sm"
                disabled={subiendo}
                onClick={() => setConfirmandoEliminar(true)}
              >
                Eliminar
              </Boton>
            )}
          </div>
        )}
      </div>

      <ConfirmacionDialog
        abierto={confirmandoEliminar}
        titulo="Eliminar logo"
        mensaje="Esta acción no se puede deshacer. El estudio quedará mostrando el ícono por defecto en el menú y en la pantalla de acceso hasta que subas uno nuevo."
        cargando={subiendo}
        onConfirmar={eliminarLogo}
        onCancelar={() => setConfirmandoEliminar(false)}
        textoConfirmar="Eliminar logo"
        peligroso
      />
    </div>
  );
}
